import { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Paper,
  Checkbox, Chip, Autocomplete, TextField, Switch, Tooltip, Link, MenuItem, Alert,
  CircularProgress,
} from '@mui/material';
import {
  UploadFile as UploadIcon,
  CloudUpload as TalanaIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import formatRut from '../formatRut';
import { formatDate } from '../formatDate';

const POLL_MS = 3000;
const IN_PROGRESS = ['queued', 'sending'];
const MAX_CATEGORY_LENGTH = 100;
// Mismos tipos que la sección Documentos de la ficha
const DOC_TYPES = ['id_card', 'pension_certificate', 'health_certificate', 'visa', 'other'];

const normalizeText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

/** Texto escrito → categoría conocida si coincide salvo mayúsculas/tildes (igual que el backend) */
export const canonicalCategory = (value, categories = []) => {
  const text = String(value ?? '').replace(/\s+/g, ' ').trim();
  const known = categories.find((c) => normalizeText(c.value) === normalizeText(text));
  return known ? known.value : text;
};

/** Visibilidad por defecto de una categoría conocida; null si no se conoce */
export const knownVisibility = (value, categories = []) => {
  const known = categories.find((c) => normalizeText(c.value) === normalizeText(value));
  return known ? known.visible : null;
};

const isSelectable = (doc) => doc.sendable && !['sent', ...IN_PROGRESS].includes(doc.talana?.status);
const rowKeyOf = (person, doc) => `${person.employeeRecordId || person.contractIds?.[0] || ''}|${doc.key}`;

/** Estado de un documento en Talana (chip); nada si aún no se envía */
export const TalanaDocStatusChip = ({ talana }) => {
  const { t } = useTranslation();
  const status = talana?.status;
  if (!status || status === 'not_sent') return null;
  if (status === 'sent') {
    return (
      <Tooltip title={talana.linkedToContract ? t('erp.export.talana.docs.linkedHint') : ''}>
        <Chip size="small" color="success" variant="outlined"
          label={t('erp.export.talana.docs.status.sent', { date: formatDate(talana.sentAt) })} />
      </Tooltip>
    );
  }
  if (IN_PROGRESS.includes(status)) {
    return (
      <Chip size="small" color="info" variant="outlined"
        icon={status === 'sending' ? <CircularProgress size={12} color="inherit" /> : undefined}
        label={t(`erp.export.talana.docs.status.${status}`)} />
    );
  }
  return (
    <Tooltip title={talana.error?.message || ''}>
      <Chip size="small" color="error" variant="outlined" label={t('erp.export.talana.docs.status.failed')} />
    </Tooltip>
  );
};

/**
 * Documentos de la persona → Talana (PLAN_DOCUMENTOS_TALANA.md §4.1 y §4.3).
 *
 * - mode="export": al «Enviar a Talana» contratos (también Reintentar y
 *   Reenviar). Una sección por persona con sus documentos de la ficha, del ATS
 *   y el contrato firmado; casilla, categoría (lista + texto libre) y
 *   «Visible». Confirmar devuelve al padre { contractId: [documentos] }: los
 *   personales van con el primer contrato de la persona en el lote y el
 *   contrato firmado con el suyo.
 * - mode="documents": una persona cuyo contrato ya está en Talana (ficha o
 *   «Reintentar documentos»). Confirmar los envía en segundo plano y la
 *   ventana muestra el avance.
 *
 * «Subir documento» guarda el archivo en la ficha (queda en Amanda) y lo deja
 * marcado. Los ya enviados no se pueden volver a marcar (no se duplican).
 */
const ErpTalanaDocumentsDialog = ({
  open,
  onClose,
  mode = 'export',
  contractIds = [],
  employeeRecordId = null,
  preselectKeys = null,
  onConfirmExport,
  onSent,
  onDocumentsChanged,
}) => {
  const { t } = useTranslation();
  const { organization, authToken, showAlert } = useStore.getState();
  const headers = { headers: { Authorization: `Bearer ${authToken}` } };

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [data, setData] = useState(null); // { configured, environment, categories, people }
  const [rows, setRows] = useState({}); // rowKey → { checked, categoria, visible, visibleTouched }
  const [uploadTypes, setUploadTypes] = useState({}); // employeeRecordId → tipo
  const [uploadingFor, setUploadingFor] = useState(null);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);
  const uploadTargetRef = useRef(null);
  const wasInProgressRef = useRef(false);

  const categories = data?.categories || [];
  const people = data?.people || [];

  const initialRow = (doc, cats) => ({
    checked: isSelectable(doc) && (preselectKeys ? preselectKeys.includes(doc.key) : true),
    categoria: doc.talana?.categoria || doc.suggestedCategory || '',
    visible: doc.talana?.puedeVerloElTrabajador ?? knownVisibility(doc.suggestedCategory, cats) ?? doc.suggestedVisible ?? true,
    visibleTouched: false,
  });

  /**
   * Carga (o recarga) la lista. keep: conserva lo que el usuario ya eligió;
   * beforeKeys: { employeeRecordId: Set(llaves que ya estaban) } → marca los
   * documentos nuevos (recién subidos).
   */
  const load = async ({ keep = false, beforeKeys = null } = {}) => {
    try {
      if (!keep) setLoading(true);
      setLoadError(null);
      const response = mode === 'export'
        ? await api.post('/erp/talana/documents/list', { organizationId: organization, contractIds }, headers)
        : await api.get(`/erp/employee-records/${employeeRecordId}/talana-documents`, {
          params: { organizationId: organization },
          ...headers,
        });
      // Mismo formato que export-talana: el objeto directo en response.data.response
      const body = response.data.response || {};
      const nextPeople = mode === 'export' ? body.people || [] : (body.person ? [body.person] : []);
      const cats = body.categories || [];
      setData({ ...body, people: nextPeople });
      setRows((prev) => {
        const next = {};
        nextPeople.forEach((person) => {
          (person.documents || []).forEach((doc) => {
            const key = rowKeyOf(person, doc);
            const isNew = beforeKeys && beforeKeys[person.employeeRecordId] && !beforeKeys[person.employeeRecordId].has(doc.key);
            if (keep && prev[key]) {
              next[key] = { ...prev[key], checked: prev[key].checked && isSelectable(doc) };
            } else {
              next[key] = initialRow(doc, cats);
            }
            if (isNew && isSelectable(doc)) next[key] = { ...next[key], checked: true };
          });
        });
        return next;
      });
    } catch (err) {
      console.error(err);
      setLoadError(t('erp.export.talana.docs.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setData(null);
      setRows({});
      wasInProgressRef.current = false;
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, employeeRecordId, contractIds.join(',')]);

  // Envío desde la ficha: consultar el avance mientras haya documentos en cola o enviándose
  const anyInProgress = people.some((p) => (p.documents || []).some((d) => IN_PROGRESS.includes(d.talana?.status)));
  useEffect(() => {
    if (!open || mode !== 'documents') return undefined;
    if (anyInProgress) {
      wasInProgressRef.current = true;
      const timer = setInterval(() => load({ keep: true }), POLL_MS);
      return () => clearInterval(timer);
    }
    if (wasInProgressRef.current) {
      wasInProgressRef.current = false;
      if (onSent) onSent();
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, anyInProgress]);

  const updateRow = (key, patch) => setRows((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const changeCategory = (key, value) => {
    setRows((prev) => {
      const row = prev[key] || {};
      const next = { ...row, categoria: value };
      // La visibilidad sigue a la categoría conocida mientras no se toque a mano
      const visible = knownVisibility(value, categories);
      if (!row.visibleTouched && visible !== null) next.visible = visible;
      return { ...prev, [key]: next };
    });
  };

  const selected = [];
  people.forEach((person) => (person.documents || []).forEach((doc) => {
    const row = rows[rowKeyOf(person, doc)];
    if (row?.checked && isSelectable(doc)) selected.push({ person, doc, row });
  }));
  const invalidSelection = selected.some(({ row }) => {
    const value = String(row.categoria || '').trim();
    return !value || value.length > MAX_CATEGORY_LENGTH;
  });

  const buildDocumentsMap = () => {
    const map = {};
    selected.forEach(({ person, doc, row }) => {
      const ids = person.contractIds || [];
      if (ids.length === 0) return;
      const target = doc.source === 'contract' && ids.includes(doc.contractId) ? doc.contractId : ids[0];
      if (!map[target]) map[target] = [];
      map[target].push({
        key: doc.key,
        categoria: canonicalCategory(row.categoria, categories),
        puedeVerloElTrabajador: Boolean(row.visible),
      });
    });
    return map;
  };

  const handleUploadClick = (person) => {
    uploadTargetRef.current = person;
    fileInputRef.current?.click();
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const person = uploadTargetRef.current;
    if (!file || !person?.employeeRecordId) return;
    try {
      setUploadingFor(person.employeeRecordId);
      const fd = new FormData();
      fd.append('document', file);
      fd.append('organizationId', organization);
      fd.append('type', uploadTypes[person.employeeRecordId] || 'other');
      const response = await api.post(
        `/erp/employee-records/${person.employeeRecordId}/documents?organizationId=${organization}`,
        fd,
        { headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' } }
      );
      if (response.data.response?.status_response === 'OK') {
        showAlert(t('erp.export.talana.docs.uploadSuccess'), 'success');
        const beforeKeys = { [person.employeeRecordId]: new Set((person.documents || []).map((d) => d.key)) };
        await load({ keep: true, beforeKeys });
        if (onDocumentsChanged) onDocumentsChanged(response.data.response.data);
      } else {
        showAlert(t('erp.export.talana.docs.uploadError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('erp.export.talana.docs.uploadError'), 'error');
    } finally {
      setUploadingFor(null);
    }
  };

  const handleSendDocuments = async () => {
    const documents = selected.map(({ doc, row }) => ({
      key: doc.key,
      categoria: canonicalCategory(row.categoria, categories),
      puedeVerloElTrabajador: Boolean(row.visible),
    }));
    try {
      setSending(true);
      const response = await api.post(
        `/erp/employee-records/${employeeRecordId}/talana-documents/send`,
        { organizationId: organization, documents },
        headers
      );
      const body = response.data.response || {};
      if (body.person) {
        setData({ ...body, people: [body.person] });
        setRows((prev) => {
          const next = { ...prev };
          (body.person.documents || []).forEach((doc) => {
            const key = rowKeyOf(body.person, doc);
            if (next[key]) next[key] = { ...next[key], checked: next[key].checked && isSelectable(doc) };
          });
          return next;
        });
      }
      if (body.accepted > 0) showAlert(t('erp.export.talana.docs.sendStarted', { count: body.accepted }), 'info');
      else showAlert(t('erp.export.talana.docs.nothingToSend'), 'info');
      if ((body.ignored || []).length > 0 && body.accepted > 0) {
        showAlert(t('erp.export.talana.docs.someIgnored', { count: body.ignored.length }), 'warning');
      }
    } catch (err) {
      console.error(err);
      const code = err.response?.data?.response?.code;
      const messages = {
        NOT_CONFIGURED: 'erp.talana.notConfigured',
        NOT_IN_TALANA: 'erp.export.talana.docs.notInTalana',
        ALREADY_RUNNING: 'erp.export.talana.docs.alreadyRunning',
        INVALID_DOCUMENTS: 'erp.export.talana.docs.invalid',
      };
      showAlert(t(messages[code] || 'erp.export.talana.docs.sendError'), code === 'ALREADY_RUNNING' ? 'info' : 'error');
    } finally {
      setSending(false);
    }
  };

  const docLabel = (doc) => {
    if (doc.source === 'contract') {
      return doc.contractStartDate
        ? t('erp.export.talana.docs.signedContractFrom', { date: formatDate(doc.contractStartDate) })
        : t('erp.export.talana.docs.signedContract');
    }
    if (doc.source === 'record' && doc.docType && doc.docType !== 'other') {
      return `${t(`erp.employees.docs.types.${doc.docType}`)} · ${doc.name}`;
    }
    return doc.name;
  };

  const renderDoc = (person, doc) => {
    const key = rowKeyOf(person, doc);
    const row = rows[key] || initialRow(doc, categories);
    const selectable = isSelectable(doc);
    const categoryValue = String(row.categoria || '');
    const categoryError = row.checked && selectable && (!categoryValue.trim() || categoryValue.length > MAX_CATEGORY_LENGTH);
    return (
      <Box key={key} data-testid={`talana-doc-${doc.key}`}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, flexWrap: 'wrap', borderTop: '1px solid #F1F2F4' }}>
        <Checkbox size="small" checked={Boolean(row.checked && selectable)} disabled={!selectable}
          inputProps={{ 'aria-label': docLabel(doc) }}
          onChange={(e) => updateRow(key, { checked: e.target.checked })} />
        <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {doc.url ? (
              <Link href={doc.url} target="_blank" rel="noopener" underline="hover" variant="body2" sx={{ wordBreak: 'break-word' }}>
                {docLabel(doc)}
              </Link>
            ) : (
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{docLabel(doc)}</Typography>
            )}
            <Chip size="small" variant="outlined" label={t(`erp.export.talana.docs.source.${doc.source}`)} />
            <TalanaDocStatusChip talana={doc.talana} />
          </Box>
          {!doc.sendable && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t(`erp.export.talana.docs.notSendable.${doc.reason || 'format'}`)}
            </Typography>
          )}
          {doc.sendable && doc.format === 'image' && selectable && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('erp.export.talana.docs.imageNote')}
            </Typography>
          )}
          {doc.talana?.status === 'failed' && doc.talana?.error?.message && (
            <Typography variant="caption" color="error" sx={{ display: 'block', whiteSpace: 'normal' }}>
              {doc.talana.error.message}
            </Typography>
          )}
        </Box>
        {selectable && (
          <>
            <Autocomplete
              freeSolo
              size="small"
              options={categories.map((c) => c.value)}
              value={categoryValue}
              inputValue={categoryValue}
              onChange={(e, value) => changeCategory(key, value || '')}
              onInputChange={(e, value, reason) => {
                if (reason === 'input' || reason === 'clear') changeCategory(key, value);
              }}
              onBlur={() => changeCategory(key, canonicalCategory(categoryValue, categories))}
              sx={{ width: 230 }}
              renderInput={(params) => (
                <TextField {...params} label={t('erp.export.talana.docs.category')} error={categoryError}
                  inputProps={{ ...params.inputProps, maxLength: MAX_CATEGORY_LENGTH }}
                  helperText={categoryError ? t('erp.export.talana.docs.categoryRequired') : undefined} />
              )}
            />
            <Tooltip title={t('erp.export.talana.docs.visibleHint')}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Switch size="small" checked={Boolean(row.visible)}
                  inputProps={{ 'aria-label': `${t('erp.export.talana.docs.visible')} · ${docLabel(doc)}` }}
                  onChange={(e) => updateRow(key, { visible: e.target.checked, visibleTouched: true })} />
                <Typography variant="caption" color="text.secondary">{t('erp.export.talana.docs.visible')}</Typography>
              </Box>
            </Tooltip>
          </>
        )}
      </Box>
    );
  };

  const renderPerson = (person) => (
    <Paper key={person.employeeRecordId || person.contractIds?.[0]} elevation={0}
      sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
        <Typography variant="subtitle2" fontWeight={700}>{person.workerName || '—'}</Typography>
        <Typography variant="body2" color="text.secondary">{formatRut(person.workerRut)}</Typography>
        {mode === 'export' && (person.contractIds || []).length > 1 && (
          <Chip size="small" variant="outlined" label={t('erp.export.talana.docs.contractsCount', { count: person.contractIds.length })} />
        )}
      </Box>
      {person.error === 'RECORD_NOT_FOUND' ? (
        <Alert severity="warning" sx={{ mt: 1 }}>{t('erp.export.talana.docs.recordNotFound')}</Alert>
      ) : (
        <>
          {(person.documents || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>{t('erp.export.talana.docs.empty')}</Typography>
          ) : (
            (person.documents || []).map((doc) => renderDoc(person, doc))
          )}
          {person.employeeRecordId && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <TextField select size="small" label={t('erp.export.talana.docs.uploadType')}
                value={uploadTypes[person.employeeRecordId] || 'other'}
                onChange={(e) => setUploadTypes((prev) => ({ ...prev, [person.employeeRecordId]: e.target.value }))}
                sx={{ minWidth: 200 }}>
                {DOC_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>{t(`erp.employees.docs.types.${type}`)}</MenuItem>
                ))}
              </TextField>
              <Tooltip title={t('erp.export.talana.docs.uploadHint')}>
                <span>
                  <Button size="small" variant="outlined" startIcon={uploadingFor === person.employeeRecordId ? <CircularProgress size={14} /> : <UploadIcon />}
                    disabled={Boolean(uploadingFor) || sending} onClick={() => handleUploadClick(person)}
                    sx={{ textTransform: 'none' }}>
                    {t('erp.export.talana.docs.upload')}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
        </>
      )}
    </Paper>
  );

  const contractsCount = contractIds.length;
  const docsCount = selected.length;
  const person = people[0];
  const title = mode === 'export'
    ? t('erp.export.talana.docs.title')
    : t('erp.export.talana.docs.titlePerson', { name: person?.workerName || '' });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {mode === 'export' ? t('erp.export.talana.docs.intro') : t('erp.export.talana.docs.introDocuments')}
        </Typography>
        {data && data.configured === false && (
          <Alert severity="warning" sx={{ mb: 2 }}>{t('erp.talana.notConfigured')}</Alert>
        )}
        {mode === 'documents' && person && !person.canSend && (
          <Alert severity="info" sx={{ mb: 2 }}>{t('erp.export.talana.docs.notInTalana')}</Alert>
        )}
        {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 4, justifyContent: 'center' }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">{t('erp.export.talana.docs.loading')}</Typography>
          </Box>
        ) : (
          people.map(renderPerson)
        )}
        <input ref={fileInputRef} type="file" hidden onChange={handleUpload} data-testid="talana-docs-file-input" />
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {mode === 'documents' ? t('erp.export.talana.docs.close') : t('erp.common.cancel')}
        </Button>
        {mode === 'export' ? (
          <>
            {docsCount > 0 && (
              <Button variant="outlined" disabled={loading || Boolean(uploadingFor)}
                onClick={() => onConfirmExport && onConfirmExport({})} sx={{ textTransform: 'none' }}>
                {t('erp.export.talana.docs.sendWithoutDocs')}
              </Button>
            )}
            <Button variant="contained" startIcon={<TalanaIcon />}
              disabled={loading || Boolean(uploadingFor) || invalidSelection || contractsCount === 0}
              onClick={() => onConfirmExport && onConfirmExport(buildDocumentsMap())}
              sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}>
              {docsCount > 0
                ? t('erp.export.talana.docs.sendWithDocs', { contracts: contractsCount, docs: docsCount })
                : t('erp.export.talana.docs.sendContracts', { contracts: contractsCount })}
            </Button>
          </>
        ) : (
          <Button variant="contained" startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <TalanaIcon />}
            disabled={loading || sending || Boolean(uploadingFor) || invalidSelection || docsCount === 0
              || !person?.canSend || data?.configured === false}
            onClick={handleSendDocuments}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}>
            {t('erp.export.talana.docs.sendDocuments', { count: docsCount })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ErpTalanaDocumentsDialog;
